import { MonetizableEnum } from '@repo/segment/enums';
import {
  MarketplaceBankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
} from '@repo/supergraph/marketplace/enums';
import type { MarketplaceEntityTypeWithDriversAndCtaOverride } from '@repo/supergraph/marketplace/types';
import type { ProductCard } from '@repo/supergraph/roundup/base/types';
import { StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import type { Graph, ItemList, ListItem, Product, WithContext } from 'schema-dts';
import { ORGANIZATION_ID } from '../constants';
import { extractStructuredContent } from '../utils';
import { formatLabel, TERM_LENGTH_FORMAT } from './utils';

export const SLUGS_TO_EXCLUDE_ITEMLIST = [
  'high-yield-online-savings-accounts',
  'money-market-accounts',
  'rewards-checking-accounts',
  '1-year-cd-rates',
  'banks-to-avoid-atm-fees',
  'national-banks',
  'no-penalty-cds',
  'kids-savings-accounts',
  '6-month-cd-rates',
  'credit-unions',
  '5-year-cd-rates',
];

const shouldNotIncludeItemList = (url: string): boolean => {
  return SLUGS_TO_EXCLUDE_ITEMLIST.some(slug => url.includes(`/banking/best/${slug}`));
};

type GetListItemSchema = {
  entity: MarketplaceEntityTypeWithDriversAndCtaOverride;
  url: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
};

const getBankingProductSchema = ({
  entity,
  url,
  pros,
  cons,
}: GetListItemSchema): WithContext<ListItem> => {
  if (entity.entityType === MarketplaceEntityTypeEnum.Institutions) {
    const category = entity.institutionType
      ? entity.institutionType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      : 'Banking institution';

    const minimumBalance = entity.offerForCta?.minimumBalance || 0;
    const unitText = `${entity.offerForCta?.driverMinimumBalance?.value || 'No'} minimum deposit required to open`;
    return getItemListSchema({
      entity,
      url,
      category,
      description: `${entity.displayName} - ${category}`,
      minimumBalance,
      unitText,
    });
  }

  let category = entity.subProductTypeLabel || 'Banking account';
  let description = `${entity.displayName} - ${category}`;

  if (entity.subProductType === MarketplaceBankingSubProductTypeEnum.CD) {
    const certificateLabel =
      entity.product?.institution?.institutionType === 'credit_union'
        ? 'Share Certificates'
        : 'Certificates of Deposit';
    const termLengthLabel = formatLabel({
      cdDepositTerm: entity.cdDepositTerm,
      format: TERM_LENGTH_FORMAT.hyphen,
      displayName: entity.displayName,
    });

    const termLengthDescription = termLengthLabel ? `- ${termLengthLabel}` : '';

    category = certificateLabel;
    description = `${entity.displayName} - ${certificateLabel} ${termLengthDescription}`;
  }

  const minimumBalance = entity.minimumBalance || 0;
  const unitText = `${entity.driverMinimumBalance?.value || 'No'} minimum deposit required to open`;

  return getItemListSchema({
    entity,
    url,
    category,
    description,
    minimumBalance,
    unitText,
    pros,
    cons,
  });
};

const getItemListSchema = ({
  entity,
  url,
  category,
  description,
  minimumBalance,
  unitText,
  pros,
  cons,
}: {
  entity: ProductCard['marketplaceEntity'];
  url: string;
  category: string;
  description: string;
  minimumBalance: number;
  unitText: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
}): WithContext<ListItem> => {
  const ratingValue = entity.starRating;
  return {
    '@context': 'https://schema.org',
    '@type': 'ListItem',
    item: {
      '@type': 'Product',
      name: entity.displayName,
      image: entity.image?.source,
      url: `${url}#${entity.id}`,
      identifier: entity.id,
      category,
      description,
      offers: [
        {
          '@type': 'Offer',
          availability:
            entity.monetizable === MonetizableEnum.yesAssumed
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: minimumBalance || 0,
            priceCurrency: 'USD',
            unitText,
          },
        },
      ],
      ...(ratingValue && {
        review: {
          '@type': 'Review',
          author: {
            '@id': ORGANIZATION_ID,
          },
          reviewRating: {
            '@type': 'Rating',
            ratingValue,
            bestRating: 5,
          },
          ...(pros && {
            positiveNotes: {
              '@type': 'ItemList',
              itemListElement: pros.map((pro: StructuredContentDocument, idx: number) => ({
                '@type': 'ListItem',
                position: idx,
                name: extractStructuredContent(pro),
              })),
            },
          }),
          ...(cons && {
            negativeNotes: {
              '@type': 'ItemList',
              itemListElement: cons.map((con: StructuredContentDocument, idx: number) => ({
                '@type': 'ListItem',
                position: idx,
                name: extractStructuredContent(con),
              })),
            },
          }),
        },
      }),
    },
  };
};

const getProductSchema = ({
  entity,
  url,
  category,
  description,
  pros,
  cons,
}: {
  entity: ProductCard['marketplaceEntity'];
  url: string;
  category: string;
  description: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
}): Product => {
  const ratingValue = entity.starRating;
  return {
    '@type': 'Product',
    name: entity.displayName,
    image: entity.image?.source,
    url: `${url}#${entity.id}`,
    identifier: entity.id,
    category,
    description,
    ...(ratingValue && {
      review: {
        '@type': 'Review',
        author: {
          '@id': ORGANIZATION_ID,
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue,
          bestRating: 5,
        },
        ...(pros && {
          positiveNotes: {
            '@type': 'ItemList',
            itemListElement: pros.map((pro: StructuredContentDocument, idx: number) => ({
              '@type': 'ListItem',
              position: idx,
              name: extractStructuredContent(pro),
            })),
          },
        }),
        ...(cons && {
          negativeNotes: {
            '@type': 'ItemList',
            itemListElement: cons.map((con: StructuredContentDocument, idx: number) => ({
              '@type': 'ListItem',
              position: idx,
              name: extractStructuredContent(con),
            })),
          },
        }),
      },
    }),
  };
};

const getBankingProductSchemaWithoutListItem = ({
  entity,
  url,
  pros,
  cons,
}: GetListItemSchema): Product => {
  if (entity.entityType === MarketplaceEntityTypeEnum.Institutions) {
    const category = entity.institutionType
      ? entity.institutionType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      : 'Banking institution';

    return getProductSchema({
      entity,
      url,
      category,
      description: `${entity.displayName} - ${category}`,
    });
  }

  let category = entity.subProductTypeLabel || 'Banking account';
  let description = `${entity.displayName} - ${category}`;

  if (entity.subProductType === MarketplaceBankingSubProductTypeEnum.CD) {
    const certificateLabel =
      entity.product?.institution?.institutionType === 'credit_union'
        ? 'Share Certificates'
        : 'Certificates of Deposit';
    const termLengthLabel = formatLabel({
      cdDepositTerm: entity.cdDepositTerm,
      format: TERM_LENGTH_FORMAT.hyphen,
      displayName: entity.displayName,
    });

    const termLengthDescription = termLengthLabel ? `- ${termLengthLabel}` : '';

    category = certificateLabel;
    description = `${entity.displayName} - ${certificateLabel} ${termLengthDescription}`;
  }

  return getProductSchema({
    entity,
    url,
    category,
    description,
    pros,
    cons,
  });
};

export const getProductListSchema = ({
  offers,
  title,
  url,
}: {
  offers: ProductCard[];
  title: string;
  url: string;
}): WithContext<ItemList> | Graph => {
  if (shouldNotIncludeItemList(url)) {
    const products = offers.map(offer =>
      getBankingProductSchemaWithoutListItem({
        entity: offer.marketplaceEntity,
        url,
        pros: offer.pros,
        cons: offer.cons,
      }),
    );

    return {
      '@context': 'https://schema.org',
      '@graph': products,
    };
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${url}#product-list`,
    name: title,
    numberOfItems: offers.length,
    itemListElement: offers.map((offer, index) => {
      const listItemSchema = getBankingProductSchema({
        entity: offer.marketplaceEntity,
        url,
        pros: offer.pros,
        cons: offer.cons,
      });

      return {
        ...listItemSchema,
        position: index + 1,
      };
    }),
  };
};
